import React, { useEffect } from 'react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Check, Power, Settings, Zap, Gauge, Activity, Ruler, TrendingUp, TrendingDown, Info, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ResponsiveImage from '../components/ResponsiveImage';
import ImageCarousel from '../components/ImageCarousel';
import BannersCarousel from '../components/BannersCarousel';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const BJ40Plus = () => {
    const { language } = useLanguage();
    const isRTL = language === 'ar';
    const t = translations[language].models.bj40Plus;
    const [activeFeature, setActiveFeature] = useState(null);

    // Initial log to confirm component mount
    useEffect(() => {
        console.log("BJ40Plus Component Mounted");
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="bg-white text-gray-900 font-sans selection:bg-red-100 overflow-x-hidden mt-20 lg:mt-[120px]">
            <SEO
                title="BAIC BJ40 Honor (Plus) Oman | Iconic Off-Road SUV"
                description="The BAIC BJ40 Honor (Plus) combines classic design with modern capability. Explore the off-road legend in Oman."
                canonicalUrl="https://baicoman.com/off-road/bj40-honor"
            />
            <h1 className="sr-only">BAIC BJ40 Honor Off-Road SUV</h1>

            {/* 1. Hero / Color Selection - Stacked Center */}
            {/* 1. Hero Section - Main Banner */}
            <BannersCarousel
                banners={[
                    {
                        image: "/assets/images/bj40-honor/banners/BJ40 Honor1.jpg",
                        title: (
                            <>
                                {t.hero.title.split(' ')[0]} <span className="text-red-600">{t.hero.title.split(' ').slice(1).join(' ')}</span>
                            </>
                        ),
                        subtitle: t.hero.subtitle
                    },
                    {
                        image: "/assets/images/bj40-honor/banners/BJ40 Honor Interior2.jpg",
                        title: (
                            <>
                                {t.hero.title.split(' ')[0]} <span className="text-red-600">{t.hero.title.split(' ').slice(1).join(' ')}</span>
                            </>
                        ),
                        subtitle: t.hero.subtitle
                    },
                    {
                        image: "/assets/images/bj40-honor/banners/BJ40 Honor Rear3.jpg",
                        title: (
                            <>
                                {t.hero.title.split(' ')[0]} <span className="text-red-600">{t.hero.title.split(' ').slice(1).join(' ')}</span>
                            </>
                        ),
                        subtitle: t.hero.subtitle
                    }
                ]}
                use1080={true}
            />

            {/* 9 Feature Sections */}
            <div className="flex flex-col">
                {[
                    { image: "/assets/images/bj40-honor/BJ40-plus-image1.png", position: "left-bottom" },
                    { image: "/assets/images/bj40-honor/BJ40-plus-image2.png", position: "right-bottom" },
                    { image: "/assets/images/bj40-honor/BJ40-plus-image3.png", position: "left-bottom" },
                    { image: "/assets/images/bj40-honor/BJ40-plus-image4.png", position: "right-bottom" },
                    { image: "/assets/images/bj40-honor/BJ40-plus-image5.png", position: "left-bottom" },
                    { image: "/assets/images/bj40-honor/BJ40-plus-image6.png", position: "right-bottom" },
                    { image: "/assets/images/bj40-honor/BJ40-plus-image7.png", position: "left-bottom" },
                    { image: "/assets/images/bj40-honor/BJ40-plus-image8.png", position: "right-bottom" },
                    { image: "/assets/images/bj40-honor/BJ40-plus-image9.png", position: "left-bottom" }
                ].map((item, index) => {
                    const feature = t.features && t.features[index];
                    if (!feature) return null;

                    // Positioning Logic
                    const position = item.position || 'left-center';
                    const [alignX, alignY] = position.split('-');

                    // Gradient Direction
                    const gradientClass = alignX === 'right'
                        ? 'bg-gradient-to-l from-black/50 via-black/10 to-transparent'
                        : 'bg-gradient-to-r from-black/50 via-black/10 to-transparent';

                    // Text Alignment (Flex)
                    let justifyClass = 'justify-center';
                    if (alignY === 'top') justifyClass = 'justify-start pt-12 md:pt-24';
                    if (alignY === 'bottom') justifyClass = 'justify-end pb-4 md:pb-24';

                    let itemsClass = 'items-start';
                    if (alignX === 'right') itemsClass = 'items-end text-right';

                    return (
                        <section key={index} className="w-full relative group overflow-hidden">
                            <ResponsiveImage
                                use1080={true}
                                image={item.image}
                                imageAlt={typeof feature.title === 'string' ? feature.title : `Feature ${index + 1}`}
                            />
                            <div dir="ltr" className={`absolute inset-0 ${gradientClass}`}>
                                <div className={`w-full px-6 h-full flex flex-col ${justifyClass} ${itemsClass}`}>
                                    <motion.div
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                        initial={{ opacity: 0, x: alignX === 'right' ? 30 : -30 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.7 }}
                                        viewport={{ once: true }}
                                        className={`max-w-xl text-white ${isRTL ? 'text-right' : ''}`}
                                    >
                                        <div className="mb-3 md:mb-4 lg:mb-6">
                                            <h2 className="text-sm md:text-3xl lg:text-5xl font-bold leading-tight">
                                                {feature.title}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log("Feature info clicked:", feature.title);
                                                        setActiveFeature(feature);
                                                    }}
                                                    className="md:hidden text-white/80 hover:text-white ml-2 inline-flex align-text-bottom"
                                                >
                                                    <Info size={16} />
                                                </button>
                                            </h2>
                                        </div>
                                        <div className="hidden md:block text-sm md:text-base lg:text-xl text-gray-200 leading-relaxed font-light">
                                            {Array.isArray(feature.desc) ? (
                                                <ul className={`list-none space-y-1 ${(isRTL || alignX === 'right') ? 'text-right' : 'text-left'}`}>
                                                    {feature.desc.map((line, i) => (
                                                        <li key={i}>{line}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                feature.desc
                                            )}
                                        </div>
                                    </motion.div>
                                </div>
                            </div>
                        </section>
                    );
                })}
            </div>

            {/* Mobile Feature Popup */}
            {activeFeature && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 md:hidden backdrop-blur-sm">
                    <div className="bg-white text-gray-900 rounded-xl p-6 max-w-sm w-full relative shadow-2xl animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setActiveFeature(null)}
                            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                        >
                            <X size={24} />
                        </button>
                        <h3 className="text-xl font-bold mb-4 pr-8">{activeFeature.title}</h3>
                        <div className="text-gray-600 leading-relaxed text-sm">
                            {Array.isArray(activeFeature.desc) ? (
                                <ul className="list-disc pl-5 space-y-1">
                                    {activeFeature.desc.map((line, i) => (
                                        <li key={i}>{line}</li>
                                    ))}
                                </ul>
                            ) : (
                                activeFeature.desc
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 6. Specifications - Tech Cards */}
            <section className="bg-slate-950 py-24 text-white relative overflow-hidden">
                {/* Background Grid Pattern */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <span className="text-red-600 font-bold tracking-widest uppercase">{t.sections.performance.tag}</span>
                        <h2 className="text-4xl md:text-6xl font-black mt-4">{t.sections.performance.title}</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: Power, ...t.specs[0] },
                            { icon: Settings, ...t.specs[1] },
                            { icon: Zap, ...t.specs[2] },
                            { icon: Gauge, ...t.specs[3] },
                            { icon: Activity, ...t.specs[4] },
                            { icon: Ruler, ...t.specs[5] },
                            { icon: TrendingUp, ...t.specs[6] },
                            { icon: TrendingDown, ...t.specs[7] }
                        ].map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.05 }}
                                className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-colors group"
                            >
                                <item.icon className="w-10 h-10 text-red-600 mb-6 group-hover:scale-110 transition-transform" />
                                <h3 className="text-gray-400 text-sm uppercase tracking-wider mb-2">{item.label}</h3>
                                <div className="text-2xl font-bold text-white mb-1">{item.value}</div>
                                <div className="text-gray-500 text-sm">{item.sub}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <section className="py-32 bg-red-600 text-white text-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-6xl font-black mb-8 uppercase">{t.sections.footer.title}</h2>
                    <p className="text-2xl mb-12 opacity-90 max-w-2xl mx-auto">{t.sections.footer.desc}</p>
                    <div className="flex gap-6 justify-center flex-wrap">
                        <Link to="/test-drive">
                            <Button className="bg-white text-red-600 hover:bg-gray-100 rounded-full px-12 py-8 text-xl font-bold shadow-2xl">
                                {t.sections.footer.btn}
                            </Button>
                        </Link>
                    </div>
                </motion.div>
            </section>

        </div >
    );
};

export default BJ40Plus;
